import { useEffect, useRef, useState } from 'react';
import { createPresenceV4, PRESENCE_ASSET } from './presence/presenceV4Engine';

// ============================================================================
// ARVEXIS PRESENCE — V4 fluid engine (Founder-approved after physical review)
//
// Three visual states, and only three:
//   IDLE      — "I am here."            quiet 0.5 Hz breath, nothing simulated
//   LISTENING — "I am hearing you."     micro-vibration from the member's own mic
//   RADIATING — "I am speaking to you." inverse core pump on Arvexis's own voice
//
// CONVERGING is eliminated: there is no visible processing state. Rotation is
// absolute 0.0 rev/s in every state (the shader carries no rotation term), and
// the origin is locked dead-centre. The canonical asset is the source of truth
// and is never modified — see presence/presenceV4Engine.js.
//
// Amplitude is never simulated here: audioVolume is Arvexis's real voice energy
// and memberVolume is the member's real mic energy, both supplied by Talk.
// ============================================================================

const SIZE_MAP = {
  small: 'w-32 h-32',
  medium: 'w-56 h-56 lg:w-72 lg:h-72',
  large: 'w-56 h-56 dsk:w-[22rem] dsk:h-[22rem]',
};

// Everything that is not the member speaking or Arvexis speaking is quiet presence.
// thinking / settling / waiting / error all resolve to IDLE: no processing state is shown.
const STATE_MAP = {
  speaking: 'radiating',
  radiating: 'radiating',
  listening: 'listening',
};

const ArvexisPresence = ({
  size = 'large',
  className = '',
  audioVolume = 0,
  memberVolume = 0,
  state = 'idle',
  profile = 'talk', // accepted for call-site compatibility; the presence grammar is now unified
}) => {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const [webglFailed, setWebglFailed] = useState(false);

  const presence = STATE_MAP[state] || 'idle';
  const isSpeaking = presence === 'radiating';
  const isListening = presence === 'listening';
  const staticOnly = reducedMotion || webglFailed;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // The engine is built ONCE per mount: it must never be rebuilt on a state change, or the
  // breath and the audio envelope would reset (the accepted D1/D2 continuity principle).
  useEffect(() => {
    if (reducedMotion || !canvasRef.current) return undefined;
    const engine = createPresenceV4(canvasRef.current);
    if (!engine) {
      setWebglFailed(true);
      return undefined;
    }
    engineRef.current = engine;
    const ro = new ResizeObserver(() => engine.resize());
    ro.observe(canvasRef.current);
    return () => {
      ro.disconnect();
      engine.dispose();
      engineRef.current = null;
    };
  }, [reducedMotion]);

  useEffect(() => { engineRef.current?.setState(presence); }, [presence]);
  useEffect(() => {
    engineRef.current?.setSignals(audioVolume, isListening ? memberVolume : 0);
  }, [audioVolume, memberVolume, isListening]);

  return (
    <div
      className={`relative ${SIZE_MAP[size] || SIZE_MAP.large} ${className}`}
      data-testid="arvexis-presence"
      data-state={state}
      data-presence-state={presence}
      data-presence-engine={staticOnly ? 'static' : 'v4-webgl'}
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      data-profile={profile}
    >
      {staticOnly ? (
        <img
          src={PRESENCE_ASSET}
          alt=""
          aria-hidden="true"
          draggable="false"
          className="absolute inset-0 w-full h-full pointer-events-none select-none"
          style={{ objectFit: 'contain' }}
          data-testid="arvexis-presence-orb"
        />
      ) : (
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none select-none"
          data-testid="arvexis-presence-orb"
        />
      )}
      <span className="sr-only" role="status" aria-live="polite" data-testid="arvexis-presence-status">
        {isSpeaking ? 'Arvexis is speaking' : ''}
      </span>
    </div>
  );
};

export default ArvexisPresence;
